import { useEffect, useState } from "react";
import { api, apiError, downloadFile } from "../../lib/api";
import { PageHeader, KpiCard } from "../../components/fields";
import { Download, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const weekStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

const weekLabel = (ws) => {
  const s = new Date(ws + "T12:00:00");
  const e = new Date(s); e.setDate(s.getDate() + 4);
  return `${s.toLocaleDateString(undefined, { month: "long", day: "numeric" })}–${e.toLocaleDateString(undefined, { day: "numeric" })}`;
};

const CARDS = [
  ["goals", "This Week's Priorities", "What support priorities did you focus on?"],
  ["accomplishments", "Wins & Progress", "What went well? Observable wins."],
  ["improvements", "Opportunities", "Where does the student still need additional support?"],
  ["reflection", "Next Week", "What should we continue or adjust?"],
];

export default function WeeklyReview() {
  const [students, setStudents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [week, setWeek] = useState(null);
  const [form, setForm] = useState({ student_id: "", goals: "", accomplishments: "", improvements: "", reflection: "" });
  const [saving, setSaving] = useState(false);
  const [dlWeek, setDlWeek] = useState("");
  const [downloading, setDownloading] = useState("");

  const weekOptions = () => {
    const opts = [];
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    for (let i = 0; i < 4; i++) {
      const w = new Date(d);
      w.setDate(d.getDate() - i * 7);
      opts.push(w.toISOString().slice(0, 10));
    }
    return opts;
  };

  const load = () => {
    api.get("/students").then((r) => {
      setStudents(r.data);
      setForm((f) => ({ ...f, student_id: f.student_id || r.data[0]?.id || "" }));
    });
    api.get("/weekly-reviews").then((r) => setReviews(r.data));
    api.get(`/aide/week-stats?week_start=${weekStart()}`).then((r) => setWeek(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const generateDraft = () => {
    if (!week) return;
    const notes = (week.notes || []).slice(-8).map((n) => `${n.block}: ${n.note}`).join(" ");
    setForm((f) => ({
      ...f,
      goals: f.goals || "Consistent block documentation, on-task engagement, and following the student's support snapshot priorities.",
      accomplishments: f.accomplishments || (notes ? `Documented ${week.documented} of ${week.scheduled} scheduled blocks (${week.doc_pct ?? 0}%). Observations this week: ${notes}` : `Documented ${week.documented} of ${week.scheduled} scheduled blocks.`),
      improvements: f.improvements || (week.behaviors > 0 ? `${week.behaviors} behavior/concern event${week.behaviors > 1 ? "s" : ""} logged this week — continue proactive positioning and redirection strategies.` : "Continue building independence during transitions."),
      reflection: f.reflection || "Maintain current support strategies; review the snapshot priorities with the supervisor at the next check-in.",
    }));
    toast.success("Draft generated from this week's documentation — review and edit before submitting");
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/weekly-reviews", { ...form, week_start: weekStart() });
      toast.success("Weekly review submitted");
      setForm((f) => ({ ...f, goals: "", accomplishments: "", improvements: "", reflection: "" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const downloadReport = async (fmt) => {
    const w = dlWeek || weekOptions()[0];
    setDownloading(fmt);
    try {
      await downloadFile(`/aide/weekly-report?week_start=${w}&fmt=${fmt}`, `supportpro_hub_weekly_${w}.${fmt}`);
      toast.success("Weekly report downloaded");
    } catch (e) { toast.error(apiError(e)); }
    setDownloading("");
  };

  const submittedThisWeek = reviews.some((r) => r.week_start === weekStart() && r.student_id === form.student_id);
  const filled = CARDS.filter(([k]) => form[k].trim()).length;
  const student = students.find((s) => s.id === form.student_id);

  const snapshot = week ? [
    ["Blocks Scheduled", week.scheduled],
    ["Blocks Documented", week.documented],
    ["Documentation", week.doc_pct != null ? `${week.doc_pct}%` : "—"],
    ["Behaviors Logged", week.behaviors],
    ["Communications", week.communications],
  ] : [];

  return (
    <div className="space-y-5 stagger">
      <PageHeader
        title="Weekly Review"
        subtitle={`Week of ${weekLabel(weekStart())}${student ? ` · ${student.first_name} ${student.last_name} · Grade ${student.grade}` : ""}`}
        actions={submittedThisWeek && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] px-3 py-2 rounded-full"><CheckCircle2 size={14} /> Submitted this week</span>
        )}
      />

      {week && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {snapshot.map(([label, value]) => (
            <div key={label} className="bg-white border border-[#E5E9EE] rounded-2xl p-4 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
              <p className="font-heading text-[22px] font-bold text-[#132238] leading-none">{value}</p>
              <p className="text-[12px] text-[#667085] mt-1.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <label className="text-[13px] font-semibold text-[#132238]">Student</label>
            <select data-testid="weekly-student-select" value={form.student_id} onChange={(e) => setForm((f) => ({ ...f, student_id: e.target.value }))}
              className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <button data-testid="generate-draft-btn" onClick={generateDraft} disabled={!week}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3.5 py-2.5 rounded-[10px] transition-colors duration-150 disabled:opacity-50">
            <Sparkles size={14} /> Generate Draft from Weekly Documentation
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CARDS.map(([k, label, ph]) => (
            <div key={k} className={`rounded-xl border p-4 transition-colors duration-200 ${form[k].trim() ? "border-[#C8EFE2] bg-[#EAF8F3]/40" : "border-[#E5E9EE] bg-[#F7F9FA]"}`}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] font-semibold text-[#132238]">{label}</label>
                {form[k].trim() && <CheckCircle2 size={14} className="text-[#087F5B]" />}
              </div>
              <textarea data-testid={`weekly-${k}-input`} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} rows={3} placeholder={ph}
                className="w-full rounded-lg border border-[#E5E9EE] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40 resize-y min-h-[76px]" />
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-[#667085]">{filled} of 4 sections complete</p>
          <button data-testid="submit-weekly-btn" onClick={submit} disabled={saving || !form.student_id}
            className="bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] px-6 py-2.5 text-sm transition-colors duration-150 active:scale-95 disabled:opacity-60">
            {saving ? "Submitting…" : "Submit Weekly Review"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
        <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight">Weekly Report Export</h3>
        <p className="text-[13px] text-[#667085] mt-1 mb-4">Download your weekly summary (blocks, behaviors, and your review) — formatted for DA Connect upload.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="da-report-week-select" value={dlWeek || weekOptions()[0]} onChange={(e) => setDlWeek(e.target.value)}
            className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
            {weekOptions().map((w, i) => <option key={w} value={w}>{`Week of ${w}${i === 0 ? " (this week)" : ""}`}</option>)}
          </select>
          <button data-testid="da-report-pdf-btn" onClick={() => downloadReport("pdf")} disabled={!!downloading}
            className="inline-flex items-center gap-2 bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] px-4 py-2 text-sm transition-colors duration-150 active:scale-95 disabled:opacity-60">
            <Download size={15} /> {downloading === "pdf" ? "Preparing…" : "Download PDF"}
          </button>
          <button data-testid="da-report-csv-btn" onClick={() => downloadReport("csv")} disabled={!!downloading}
            className="inline-flex items-center gap-2 border border-[#E5E9EE] hover:bg-slate-50 text-[#132238] font-semibold rounded-[10px] px-4 py-2 text-sm transition-colors duration-150 active:scale-95 disabled:opacity-60">
            <Download size={15} /> {downloading === "csv" ? "Preparing…" : "Download CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
        <h3 className="font-heading text-[17px] font-semibold text-[#132238] tracking-tight mb-4">Past Reviews</h3>
        <div className="space-y-3">
          {reviews.length === 0 && <p className="text-sm text-[#667085]">No reviews yet — your submitted reflections appear here.</p>}
          {reviews.map((r) => {
            const st = students.find((s) => s.id === r.student_id);
            return (
              <div key={r.id} className="border border-[#E5E9EE] rounded-xl p-4">
                <p className="text-sm font-semibold text-[#132238]">Week of {r.week_start} · {st ? `${st.first_name} ${st.last_name}` : ""}</p>
                <p className="text-sm text-[#667085] mt-1.5"><span className="font-medium text-[#132238]">Wins:</span> {r.accomplishments}</p>
                <p className="text-sm text-[#667085] mt-0.5"><span className="font-medium text-[#132238]">Next week:</span> {r.improvements}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
